import * as _ from 'lodash';
// eslint-disable-next-line no-restricted-imports
import moment from 'moment';
import qs from 'qs';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
// eslint-disable-next-line no-restricted-imports
import type { Moment } from 'moment';
import type { VoidFunctionComponent } from 'react';
import type { PageChangeFunction, PageSizeChangeFunction, SortedChangeFunction } from 'react-table';

import { useLocation, useRouter } from '../../../contexts/RouterContext';
import { isOrgInFreeTier } from '../../../helpers/subscriptions';
import useCreateAuditLogExport from '../../../hooks/api/settings/audit-logs/useCreateAuditLogExport';
import AuditLogModule from '../../../redux/audit_logs';
import { isTrue } from '../../../utils';
import PaidFeatureGate from '../../generic/Gates/PaidFeatureGate';
import Icon, { icons, IconText } from '../../generic/Icon';
import Select from '../../generic/Select/Select';
import { useAddJobToNotificationQueue } from '../../JobNotificationHub/Provider';
import ModalDateRangePicker from '../../modals/ModalDateRangePicker';
import Header from '../../shared/settings/SettingsPageHeader';
import type { TopicActionSelectOption, UserSelectOption } from '../../../redux/audit_logs/store';

import AuditLogTable from './AuditLogTable';

import styles from './AuditLogs.module.less';

const usedQueryParams = ['page', 'pageSize', 'startDate', 'endDate', 'topics', 'actions', 'actingUserIds', 'topicActions', 'sortBy', 'sortDir'];

type AuditLogsPageQueryState = {
  page: number;
  pageSize: number;
  startDate?: Moment;
  endDate?: Moment;
  topics?: string[];
  actingUserIds?: number[];
  actions?: string[];
  topicActions?: string[];
  sortBy?: string;
  sortDir?: string;
};

type AuditLogApiParams = {
  startDate?: string | null;
  endDate?: string | null;
  topics?: string[];
  actions?: string[];
  actingUserIds?: number[];
  topicActions?: string[];
  sortBy?: string;
  sortDir?: string;
  offset?: number;
  limit?: number;
};

const defaultQuery = {
  page: 0,
  pageSize: 10,
  topics: [],
  actions: [],
  actingUserIds: [],
  topicActions: [],
};

const dateFormat = 'YYYY-MM-DD';

function pathPartToTopics(urlSegment: string | null): string[] {
  switch (urlSegment) {
    case 'users':
      return ['User', 'Team'];
    case 'issues':
      return ['Issue'];
    default:
      return [];
  }
}

function getSidebarPathPart(pathname: string): string | null {
  const pathParts = pathname.split('/');
  const auditLogsPartIndex = pathParts.indexOf('audit-logs');
  if (auditLogsPartIndex > 0) {
    return pathParts[auditLogsPartIndex + 1] || null;
  }
  return null;
}

function getAuditLogHeading(pathname: string): 'All' | 'User' | 'Issue' {
  const lastPathSegment = pathname.split('/').pop();
  if (lastPathSegment === 'users') {
    return 'User';
  }
  if (lastPathSegment === 'issues') {
    return 'Issue';
  }
  return 'All';
}

function parseStringArray(val: qs.ParsedQs[string]): string[] | null {
  if (_.isString(val) && val) {
    return val.split(',');
  }
  if (Array.isArray(val)) {
    return val.filter(_.isString);
  }
  return null;
}

function parseQueryFromSearch(search: string, pathname: string): Partial<AuditLogsPageQueryState> {
  const sidebarPathPart = getSidebarPathPart(pathname);
  const searchString = search.startsWith('?') ? search.substr(1) : search;
  const parsed = qs.parse(searchString, { comma: true });

  const result: Partial<AuditLogsPageQueryState> = {
    topics: pathPartToTopics(sidebarPathPart),
  };

  if (_.isString(parsed.startDate) && parsed.startDate) {
    result.startDate = moment(parsed.startDate);
  }
  if (_.isString(parsed.endDate) && parsed.endDate) {
    result.endDate = moment(parsed.endDate);
  }

  (['topics', 'actions', 'topicActions'] as const).forEach(key => {
    const arr = parseStringArray(parsed[key]);
    if (arr) {
      result[key] = arr;
    }
  });

  const rawUserIds = parseStringArray(parsed.actingUserIds);
  if (rawUserIds) {
    result.actingUserIds = rawUserIds.map(x => Number.parseInt(x, 10));
  }

  if (_.isString(parsed.page) && parsed.page) {
    result.page = Number(parsed.page);
  }
  if (_.isString(parsed.pageSize) && parsed.pageSize) {
    result.pageSize = Number(parsed.pageSize);
  }
  if (_.isString(parsed.sortBy) && parsed.sortBy) {
    result.sortBy = parsed.sortBy;
  }
  if (_.isString(parsed.sortDir) && parsed.sortDir) {
    result.sortDir = parsed.sortDir;
  }

  return result;
}

function serializeQueryToSearch(query: AuditLogsPageQueryState, pathname: string): string {
  const searchParams = _.omitBy(query,
    (value, key) => key.endsWith('Date') ? value === null : _.isEmpty(value));
  const filteredParams = _.pick(searchParams, usedQueryParams);
  if (filteredParams.startDate) {
    filteredParams.startDate = moment(filteredParams.startDate).format(dateFormat);
  }
  if (filteredParams.endDate) {
    filteredParams.endDate = moment(filteredParams.endDate).format(dateFormat);
  }
  const sidebarPathPart = getSidebarPathPart(pathname);
  if (_.isEqual(filteredParams.topics, pathPartToTopics(sidebarPathPart))) {
    delete filteredParams.topics;
  }

  return qs.stringify(filteredParams, { arrayFormat: 'comma' });
}

function computeRouteQueryParams(query: AuditLogsPageQueryState) {
  const {
    startDate, endDate, topics, actions, topicActions, actingUserIds, page, pageSize, sortBy, sortDir,
  } = query;
  const startDateString = startDate ? moment(startDate).format(dateFormat) : null;
  const endDateString = endDate ? moment(endDate).format(dateFormat) : null;

  return {
    ...(_.omitBy({
      startDate: startDateString,
      endDate: endDateString,
      topics,
      actions,
      actingUserIds,
      topicActions,
      page,
      pageSize,
      sortBy,
      sortDir,
    }, _.isEmpty)),
    page,
    pageSize,
  };
}

export const AuditLogsPage: VoidFunctionComponent = () => {
  const router = useRouter();
  const location = useLocation();
  const dispatch = useDispatch();

  const status = useSelector(AuditLogModule.selectors.getStatus);
  const logCount = useSelector(AuditLogModule.selectors.getCount);
  const logs = useSelector(AuditLogModule.selectors.getLogs);
  const topicActionOptions: TopicActionSelectOption[] = useSelector(AuditLogModule.selectors.getTopicActionOptions);
  const userOptions: UserSelectOption[] = useSelector(AuditLogModule.selectors.getUserOptions);

  const addJob = useAddJobToNotificationQueue();
  const { mutateAsync: createAuditLogExport, isLoading: isExportLoading } = useCreateAuditLogExport();

  const isFreeOrg = isOrgInFreeTier();

  const [query, setQuery] = useState<AuditLogsPageQueryState>(() => ({
    startDate: moment().startOf('day').subtract(1, 'month'),
    endDate: moment().endOf('day'),
    ...defaultQuery,
    ...parseQueryFromSearch(location.search, location.pathname),
  }));
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

  const prevLocationRef = useRef(location);
  const prevQueryRef = useRef(query);
  const isMountedRef = useRef(false);

  const loadAuditLogs = useCallback((currentQuery: AuditLogsPageQueryState) => {
    const { page, pageSize, ...rest } = computeRouteQueryParams(currentQuery);
    const apiQueryParams: AuditLogApiParams = { ...rest };
    if (Number.isInteger(page)) {
      if (Number.isInteger(pageSize)) {
        apiQueryParams.limit = pageSize;
      }
      apiQueryParams.offset = page * pageSize;
    }

    return Promise.all([
      dispatch(AuditLogModule.actions.loadAuditLogs(apiQueryParams)),
      dispatch(AuditLogModule.actions.loadAuditLogCount(apiQueryParams)),
    ]);
  }, [dispatch]);

  // Load filter options and initial data on mount
  useEffect(() => {
    if (!isFreeOrg) {
      dispatch(AuditLogModule.actions.loadAuditLogTopicActionOptions());
      dispatch(AuditLogModule.actions.loadAuditLogUserFilterOptions());
      loadAuditLogs(query);
    }
    isMountedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync URL changes (e.g. browser back/forward) into component state
  useEffect(() => {
    if (!_.isEqual(location, prevLocationRef.current)) {
      prevLocationRef.current = location;
      const parsed = parseQueryFromSearch(location.search, location.pathname);
      setQuery(() => ({
        startDate: moment().startOf('day').subtract(1, 'month'),
        endDate: moment().endOf('day'),
        ...defaultQuery,
        ...parsed,
      }));
    }
  }, [location]);

  // Sync state changes to URL and reload data
  useEffect(() => {
    if (isMountedRef.current && !isFreeOrg && !_.isEqual(query, prevQueryRef.current)) {
      prevQueryRef.current = query;
      const targetSearch = `?${serializeQueryToSearch(query, location.pathname)}`;
      if (targetSearch !== location.search) {
        router.push({
          pathname: location.pathname,
          search: targetSearch,
        });
      }
      loadAuditLogs(query);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const handleChangeActingUsers = useCallback((actingUsers: Array<{ value: string }> | null) => {
    setQuery(prev => ({
      ...prev,
      actingUserIds: (actingUsers || []).map((user) => Number(user.value)),
      page: 0,
    }));
  }, []);

  const handleChangeTopicActions = useCallback((topicActions: Array<{ value: string }> | null) => {
    setQuery(prev => ({
      ...prev,
      topicActions: (topicActions || []).map((topicAction) => topicAction.value),
      page: 0,
    }));
  }, []);

  const handleChangeDateRange = useCallback((range: { startDate: Date; endDate: Date }) => {
    setQuery(prev => ({
      ...prev,
      startDate: moment(range.startDate) || '',
      endDate: moment(range.endDate) || '',
      page: 0,
    }));
  }, []);

  const handleChangeTablePage: PageChangeFunction = useCallback((pageIndex) => {
    setQuery(prev => ({
      ...prev,
      page: pageIndex,
    }));
  }, []);

  const handleChangeTablePageSize: PageSizeChangeFunction = useCallback((pageSize, pageIndex) => {
    setQuery(prev => ({
      ...prev,
      pageSize,
      page: pageIndex,
    }));
  }, []);

  const handleChangeTableSort: SortedChangeFunction = useCallback((newSorted, column) => {
    setQuery(prev => ({
      ...prev,
      sortBy: column.id,
      sortDir: isTrue(newSorted[0]?.desc) ? 'DESC' : 'ASC',
    }));
  }, []);

  const handleExportAuditLogs = useCallback(async () => {
    const newQueryParams = computeRouteQueryParams(query);
    const apiQueryParams = _.omit(
      newQueryParams,
      'page',
      'pageSize',
      'sortBy',
      'sortDir',
    );

    const response = await createAuditLogExport(apiQueryParams);

    if (response?.data?.task) {
      const { jobToken, task } = response.data.task;
      addJob(jobToken, task);
    }
  }, [query, createAuditLogExport, addJob]);

  const auditLogHeading = getAuditLogHeading(location.pathname);

  const startDate = moment(query.startDate);
  const startLabel = startDate.format('MMMM Do');
  const endDate = moment(query.endDate);
  const endLabel = endDate.format('MMMM Do');
  const dateLabel = startLabel === endLabel ? startLabel : `${startLabel} - ${endLabel}`;

  let visibleTopicActionOptions = topicActionOptions;
  if ((query.topics?.length ?? 0) > 0) {
    visibleTopicActionOptions = topicActionOptions.filter(option => query.topics?.includes(option.topic));
  }
  const selectedTopicActionOptions = topicActionOptions?.filter(option => query.topicActions?.includes(option.value));

  const groupedOptions = Object.entries(_.groupBy(visibleTopicActionOptions ?? {}, ({ topic }) => topic)).map(
    ([topic, options]) => ({
      label: _.words(topic).join(' '),
      options,
    }),
  );

  return (
    <>
      <div className={styles.header}>
        <Header heading={`${auditLogHeading} Logs`} />
      </div>
      <PaidFeatureGate isRestricted={isFreeOrg}>
        <div className="row">
          <div className="col-xs-12">
            <div className={styles.actionsBar}>
              <div className={styles.actionsBarChild}>
                <Select
                  isClearable
                  isMulti
                  id="user-filter"
                  placeholder="Select Users by email or username..."
                  menuPortalTarget={document.body}
                  options={userOptions}
                  onChange={handleChangeActingUsers}
                  value={userOptions?.filter(option => query.actingUserIds?.includes(_.toNumber(option.value)))}
                />
              </div>
              <div className={styles.actionsBarChild}>
                <Select
                  isClearable
                  isMulti
                  id="action-filter"
                  placeholder="Select Actions..."
                  menuPortalTarget={document.body}
                  options={groupedOptions}
                  onChange={handleChangeTopicActions}
                  value={selectedTopicActionOptions}
                />
              </div>
              <Button onClick={() => setIsDatePickerOpen(true)}>
                {dateLabel} <Icon icon={icons.chevronDown} />
              </Button>
              <Button
                onClick={handleExportAuditLogs}
                disabled={isExportLoading || status.loading}
              >
                <IconText icon={icons.download} text="Export logs as CSV" />
              </Button>
            </div>
            <ModalDateRangePicker
              show={isDatePickerOpen}
              onSetDateRange={handleChangeDateRange}
              startDate={startDate.format(dateFormat)}
              endDate={endDate.format(dateFormat)}
              onHide={() => setIsDatePickerOpen(false)}
            />
          </div>
        </div>
        <AuditLogTable
          pageSize={query?.pageSize || 10}
          page={query?.page || 0}
          isLoading={status.loading || false}
          onPageChange={handleChangeTablePage}
          onPageSizeChange={handleChangeTablePageSize}
          onSortedChange={handleChangeTableSort}
          logs={logs}
          logCount={logCount}
        />
      </PaidFeatureGate>
    </>
  );
};
